import React from 'react';

/**
 * Spam honeypot for the public forms (GetStarted -> POST /api/leads, ContactUs -> POST /api/contact).
 *
 * A person never sees this input and cannot tab into it, so any value that arrives means the submitter
 * filled every input it could find - a bot. linok-api drops those submissions silently (same status
 * code and body a real one gets, but nothing is stored, emailed, or posted to Slack). Driver:
 * linok-api `docs/security/01-public-form-spam-incident-2026-08-08.md`.
 *
 * The field name must stay in step with `HONEYPOT_FIELD` in linok-api `src/utils/honeypot.ts`.
 * Renaming one side alone silently disables the trap: the field simply never arrives and every
 * submission looks clean.
 *
 * Implementation notes, each of which matters:
 * - **Off-screen, not `display: none` or `type="hidden"`.** Both of those are trivially detectable and
 *   the better bots skip them; an off-screen text input looks like a real field to a DOM walker.
 * - **`tabIndex={-1}` + `aria-hidden`** so keyboard and screen-reader users cannot reach it. Without
 *   these the "invisible" field is a genuine accessibility trap, not just an invisible one.
 * - **`autoComplete="off"`** because a browser autofilling this would drop a real enquiry with no error
 *   shown to the person who wrote it. That is the one failure mode worth engineering against, which is
 *   also why the API logs the submitted email when it drops a submission - so a false positive is
 *   recoverable from the logs rather than lost.
 */
const HoneypotField = ({ value, onChange }: { value: string; onChange: (value: string) => void }) => (
    <div
        aria-hidden="true"
        style={{
            position: 'absolute',
            left: '-9999px',
            top: 'auto',
            width: '1px',
            height: '1px',
            overflow: 'hidden',
        }}
    >
        <label htmlFor="website-url-field">Website (leave this field empty)</label>
        <input
            id="website-url-field"
            type="text"
            name="website"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            tabIndex={-1}
            autoComplete="off"
        />
    </div>
);

export default HoneypotField;
